import { RFIType } from 'interfaces/RFIRequestForm';

import { getConfigValue } from '../config/appConfig';
import { DCDIVEST, DCSP, DEFAULT_BUSINESS_ID, IPS, PAC, RFXTypes } from '../data/BusinessConstants';
import { NullableString } from '../interfaces';
import { RFQEntity } from '../interfaces/index';
import { RFXRequest } from '../interfaces/RFXBackendModel';
import { RFXRequestBaseInformation } from '../interfaces/RFXRequestForm';
import {
  shouldEnableAutoNameGenerationForWorkflow,
  shouldExecuteAutoBOMImportForWorkflow,
  shouldSupportCurrencyCheckForWorkflow,
  shouldSupportIncotermForWorkflow,
  shouldSupportMultipleRegionsForWorkflow
} from './WorkflowUtils';

export const shouldSupportMultipleCategories = (businessId: NullableString | undefined): boolean => {
  return businessId === IPS;
};

/**
 * Determines if multiple regions should be supported for a given business ID.
 *
 * @param businessId - The business ID to check.
 * @returns True if the business ID supports multiple regions, false otherwise.
 */
const shouldSupportMultipleRegionsForBusinessId = (businessId: NullableString | undefined): boolean => {
  return businessId === IPS || businessId === DCSP;
};

/**
 * Determines if multiple regions should be supported based on the business ID and workflow.
 *
 * @param rfxType - rfx type, could be RFQ and RFI
 * @param businessId - The business ID to check.
 * @param workflow - The workflow to check.
 * @returns True if multiple regions should be supported, false otherwise.
 */
export const shouldSupportMultipleRegions = (rfxType: string, businessId: string, workflow: string): boolean => {
  if (rfxType === RFXTypes.RFI) {
    return true;
  }
  if (!shouldSupportMultipleRegionsForBusinessId(businessId)) {
    return false;
  }
  return shouldSupportMultipleRegionsForWorkflow(workflow);
};

/**
 * Determines if auto BOM import should be executed for a given business ID.
 *
 * @param businessId - The business ID to check.
 * @returns True if auto BOM import should be executed for the given business ID, false otherwise.
 */
export const shouldExecuteAutoBOMImportForBusinessId = (businessId: string): boolean => {
  switch (businessId) {
    case PAC:
    case DCSP:
      return true;
    default:
      break;
  }
  return false;
};

/**
 * Determines if auto BOM import should be executed based on the business ID and workflow.
 *
 * @param businessId - The business ID to check.
 * @param workflow - The workflow to check.
 * @returns True if auto BOM import should be executed for the given business ID and workflow, false otherwise.
 */
export const shouldExecuteAutoBOMImport = (businessId: string, workflow: string): boolean => {
  if (!shouldExecuteAutoBOMImportForBusinessId(businessId)) {
    return false;
  }
  return shouldExecuteAutoBOMImportForWorkflow(workflow);
};

/**
 * Retrieves the business ID from an RFX request general information object
 *
 * @param rfxRequest - The RFX request general information object
 * @returns The business ID if present, otherwise returns the default business ID
 */
export const getBusinessId = (rfxRequest: RFXRequestBaseInformation): string => {
  return rfxRequest.businessId ? rfxRequest.businessId : DEFAULT_BUSINESS_ID;
};

/**
 * Retrieves the business ID from an RFX request backend model
 *
 * @param rfxRequest - The RFX request backend model
 * @returns The business ID value if present, otherwise returns the default business ID
 */
export const getBusinessIdFromBackendModel = (rfxRequest: RFXRequest): string => {
  return rfxRequest.businessId ? rfxRequest.businessId.value : DEFAULT_BUSINESS_ID;
};

export const shouldPerformBOMValidation = (businessId: NullableString | undefined): boolean => {
  return businessId === PAC;
};
export function shouldShowQuoteStepBanner(businessId: string | null | undefined): boolean {
  return businessId === PAC;
}

export const shouldSupportLIVE = (businessId: NullableString | undefined): boolean => {
  switch (businessId) {
    case PAC:
    case DCDIVEST:
      return true;
    case IPS:
    default:
      break;
  }
  return false;
};

export function isResponseWizardStepRequired(stepName: string, businessId: string): boolean {
  switch (businessId) {
    case IPS:
      return true;
    default:
      if (stepName === 'contacts') {
        return false;
      }
      return true;
  }
}

export const shouldSupportLIVEForRFQEntity = (lineItemRelation: RFQEntity) => {
  switch (lineItemRelation) {
    case RFQEntity.REQUEST:
      return true;
    case RFQEntity.RESPONSE:
      return true;
    default:
      break;
  }
  return false;
};

export const shouldSupportPostAwardDetails = (businessId: string): boolean => {
  return businessId === IPS;
};

export const isValidRequestStatus = (requestStatus: string): boolean => {
  return requestStatus === 'Published' || requestStatus === 'Pending_Selection';
};

/**
 * Determines if Incoterm should be supported for a given business ID.
 * TODO: to disable exporting
 *
 * @param businessId - The business ID to check.
 * @returns True if Incoterm should be supported for the business ID, false otherwise.
 */
export const shouldSupportIncotermForBusinessId = (
  businessId: NullableString | undefined,
  rfxType?: string
): boolean => {
  if (rfxType !== RFXTypes.RFQ) {
    return false;
  }
  if (!getConfigValue('enableIncoterm')) {
    return false;
  }
  switch (businessId) {
    case PAC:
    case DCSP:
      return true;
    default:
      break;
  }
  return false;
};

/**
 * Determines if Incoterm should be supported based on the business ID and workflow.
 *
 * @param businessId - The business ID to check.
 * @param workflow - The workflow to check.
 * @param rfxType - RFQ or RFI
 * @returns True if Incoterm should be supported, false otherwise.
 */
export const shouldSupportIncoterm = (businessId: string, workflow: string, rfxType: string): boolean => {
  if (rfxType !== RFXTypes.RFQ) {
    return false;
  }
  if (!shouldSupportIncotermForBusinessId(businessId, rfxType)) {
    return false;
  }
  return shouldSupportIncotermForWorkflow(workflow, rfxType);
};

export const shouldAddExtraBannerInLIVE = (businessId: string, lineItemRelation: RFQEntity | null): boolean => {
  return lineItemRelation === RFQEntity.REQUEST && businessId === PAC;
};

/**
 * Determines if the product line filter is enabled based on the business ID and workflow.
 *
 * @param businessId - The ID of the business.
 * @param workflow - The current workflow.
 * @returns True if the product line filter is enabled, false otherwise.
 */
export const isProductLineFilterEnabled = (businessId: string, workflow: string, rfxType: string): boolean => {
  return rfxType === RFXTypes.RFQ && businessId === DCSP && workflow === DCSP;
};

export const isCategoriesFilterEnabled = (rfxType:string): boolean => {
  return rfxType === RFXTypes.RFI;
};

export const isCountryCodeEnabled = (rfxType:string): boolean => {
  return rfxType === RFXTypes.RFI;
};

/**
 * Determines if auto name generation should be enabled for a given business ID.
 *
 * @param businessId - The business ID to check.
 * @returns True if auto name generation should be enabled for the business ID, false otherwise.
 */
const shouldEnableAutoNameGenerationForBusinessId = (businessId: NullableString | undefined): boolean => {
  switch (businessId) {
    case PAC:
    case DCSP:
      return true;
    default:
      break;
  }
  return false;
};

/**
 * Determines if auto name generation should be enabled based on the business ID and workflow.
 *
 * @param businessId - The business ID to check.
 * @param workflow - The workflow to check.
 * @returns True if auto name generation should be enabled, false otherwise.
 */
export const shouldEnableAutoNameGeneration = (businessId: string, workflow: string): boolean => {
  if (!shouldEnableAutoNameGenerationForBusinessId(businessId)) {
    return false;
  }
  return shouldEnableAutoNameGenerationForWorkflow(workflow);
};

/**
 * Determines if vendor service currency check should be supported for a given business ID.
 *
 * @param businessId - The business ID to check.
 * @returns True if Incoterm should be supported for the business ID, false otherwise.
 */
const shouldSupportVendorServiceCurrencyValidation = (businessId: string, rfxType: string): boolean => {
  if (rfxType !== RFXTypes.RFQ) {
    return false;
  }
  if (!getConfigValue('enableCurrencyCheck')) {
    return false;
  }
  switch (businessId) {
    case PAC:
    case DCSP:
      return true;
    default:
      break;
  }
  return false;
};

/**
 * Determines if currency check should be supported based on the business ID and workflow.
 *
 * @param businessId - The business ID to check.
 * @param workflow - The workflow to check.
 * @returns True if currency check should be supported, false otherwise.
 */
export const shouldSupportCurrencyCheck = (businessId: string, workflow: string, rfxType: string): boolean => {
  if (rfxType !== RFXTypes.RFQ) {
    return false;
  }
  if (!shouldSupportVendorServiceCurrencyValidation(businessId, rfxType)) {
    return false;
  }
  return shouldSupportCurrencyCheckForWorkflow(workflow, rfxType);
};
